// AxisSection.component.tsx
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useUnits } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';
import { Category } from '@Types/category.types';
import {
	BreakdownAxis,
	BreakdownCategory,
	BreakdownComponent,
} from '@Types/riskBreakdown.types';
import {
	FormatWithUnit,
	PCT_TOLERANCE,
	ReconciledAxis,
	ScoresOnlyCategory,
	defaultNote,
	fmtScore,
	formatBand,
	formatComponentValue,
	formatPct,
	noFeatureLimit,
	roundedFmt,
} from './explanation.utils';

export interface AxisSectionProps {
	axisKey: 'LOF' | 'COF';
	axis?: BreakdownAxis;
	reconciled?: ReconciledAxis;
	scoresOnly?: ScoresOnlyCategory[];
	configCategories?: Category[];
}

const AMBER = '#9a5b25';
const COLS = '1.3fr 1.3fr auto';

// Literal keys per axis so i18next-parser can extract them.
const axisText = (t: TFunc, axisKey: 'LOF' | 'COF') =>
	axisKey === 'LOF'
		? {
				title: t('Likelihood of Failure (LoF)'),
				pctOf: (pct: string) => t('{{pct}}% of LoF', { pct }),
				pctOfApplied: (pct: string, applied: string) =>
					t('{{pct}}% of LoF ({{applied}}% applied)', {
						pct,
						applied,
					}),
				totalLabel: t('LoF Total'),
				tieBack: (weighting: string, row: string) =>
					t('× {{weighting}}% = {{row}} — the row’s LoF', {
						weighting,
						row,
					}),
				mismatch: (row: string) =>
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					t("Doesn't match the row's LoF ({{row}})", { row }),
				// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
				pointsOff: t("Points don't add up to the LoF total"),
				noScore: t('No LoF score'),
		  }
		: {
				title: t('Consequences of Failure (CoF)'),
				pctOf: (pct: string) => t('{{pct}}% of CoF', { pct }),
				pctOfApplied: (pct: string, applied: string) =>
					t('{{pct}}% of CoF ({{applied}}% applied)', {
						pct,
						applied,
					}),
				totalLabel: t('CoF Total'),
				tieBack: (weighting: string, row: string) =>
					t('× {{weighting}}% = {{row}} — the row’s CoF', {
						weighting,
						row,
					}),
				mismatch: (row: string) =>
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					t("Doesn't match the row's CoF ({{row}})", { row }),
				// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
				pointsOff: t("Points don't add up to the CoF total"),
				noScore: t('No CoF score'),
		  };

type AxisText = ReturnType<typeof axisText>;

// The value only; its band is in a tooltip (bandText).
const valueText = (
	c: BreakdownComponent,
	t: TFunc,
	fmt: FormatWithUnit,
): string => {
	// A deselected row has no value by design; never word it as a spatial miss.
	if (c.defaultReason === 'deselected') return '—';
	if (c.format === 'distance' && c.value === null && !c.usedDefault) {
		const limit = noFeatureLimit(c.band);
		return limit !== null && c.unit
			? t('None within {{distance}}', {
					distance: roundedFmt(fmt)(limit, c.unit),
			  })
			: t('None within range');
	}
	const value = formatComponentValue(c, fmt);
	if (value === null) return c.usedDefault ? t('No data') : '—';
	return c.format === 'distance'
		? t('{{distance}} to nearest', { distance: value })
		: value;
};

const bandText = (
	c: BreakdownComponent,
	t: TFunc,
	fmt: FormatWithUnit,
): string | null =>
	c.band
		? t('Band {{band}} → score {{score}}', {
				band: formatBand(c.band, c.unit, fmt),
				score: fmtScore(c.score),
		  })
		: null;

const Header = ({ cols }: { cols: string[] }) => (
	<Box
		sx={{
			display: 'grid',
			gridTemplateColumns: cols.length === 3 ? COLS : '1fr auto',
			columnGap: 2,
			pb: 0.5,
			borderBottom: '1px solid',
			borderColor: 'divider',
		}}>
		{cols.map((label, i) => (
			<Typography
				key={label}
				variant="caption"
				sx={{
					color: 'text.secondary',
					textAlign: i === cols.length - 1 ? 'right' : 'left',
				}}>
				{label}
			</Typography>
		))}
	</Box>
);

const Bar = ({ share }: { share: number }) => (
	<Box
		aria-hidden
		sx={{
			mt: 0.5,
			height: 4,
			borderRadius: 2,
			background: '#eef1f4',
			overflow: 'hidden',
		}}>
		<Box
			sx={{
				height: '100%',
				width: `${share}%`,
				background: '#9ec5ee',
				borderRadius: 2,
			}}
		/>
	</Box>
);

interface ComponentRowProps {
	component: BreakdownComponent;
	/** Displayed points, or null for a noData row. */
	points: number | null;
	axisTotal: number | null;
	useHighestScore?: boolean;
	t: TFunc;
	fmt: FormatWithUnit;
}

export const ComponentRow = ({
	component: c,
	points,
	axisTotal,
	useHighestScore,
	t,
	fmt,
}: ComponentRowProps) => {
	const note = defaultNote(c, t);
	// A deselected row is informational: its note is a neutral caption, not a ⚠ default.
	const deselected = c.defaultReason === 'deselected';
	const warning = deselected ? null : note;
	const value = valueText(c, t, fmt);
	const band = bandText(c, t, fmt);
	const share =
		axisTotal && points
			? Math.min(100, Math.max(0, (points / axisTotal) * 100))
			: 0;
	const losing =
		c.weight === 0 &&
		c.score !== null &&
		c.defaultReason !== 'noData' &&
		!deselected;
	const losingText = useHighestScore
		? t('Not counted — another component in this category scored higher')
		: t('Not counted for this asset (0% weight)');
	return (
		<Box
			data-cy="explanation-component"
			sx={{
				py: 0.5,
				borderBottom: '1px solid',
				borderColor: 'grey.100',
			}}>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: COLS,
					columnGap: 2,
				}}>
				<Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
					{c.name}
				</Typography>
				<Typography
					variant="body2"
					sx={{
						fontSize: '0.8125rem',
						color: warning ? AMBER : 'text.secondary',
						fontStyle: warning ? 'italic' : 'normal',
					}}>
					{band ? (
						<Tooltip title={band}>
							{/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tooltip trigger: focusable so keyboard users can reach the band */}
							<span data-cy="explanation-value" tabIndex={0}>
								{value}
							</span>
						</Tooltip>
					) : (
						<span data-cy="explanation-value">{value}</span>
					)}
					{warning && (
						<>
							{' '}
							<Tooltip title={warning}>
								<span
									role="img"
									// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tooltip trigger: focusable so keyboard users can reach the reason
									tabIndex={0}
									aria-label={warning}>
									⚠
								</span>
							</Tooltip>
						</>
					)}
				</Typography>
				<Typography
					variant="body2"
					sx={{
						fontSize: '0.8125rem',
						textAlign: 'right',
						color: 'text.secondary',
					}}>
					{`${fmtScore(c.score)} × ${formatPct(c.weight)}% = `}
					<b style={{ color: '#1b2733' }}>
						{points === null ? '—' : points.toFixed(2)}
					</b>
				</Typography>
			</Box>
			{deselected && (
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>
					{note}
				</Typography>
			)}
			{losing && (
				<Typography variant="caption" sx={{ color: 'text.secondary' }}>
					{losingText}
				</Typography>
			)}
			<Bar share={share} />
		</Box>
	);
};

// Amber note for a category that got less than its configured share. Only a
// Condition category dropped for missing inspection scales the others up.
const categoryNote = (
	cat: BreakdownCategory,
	isCondition: boolean | undefined,
	t: TFunc,
): string | null => {
	if (cat.percentage - cat.appliedPercentage <= PCT_TOLERANCE) return null;
	const vals = {
		category: cat.category,
		configured: formatPct(cat.percentage),
		applied: formatPct(cat.appliedPercentage),
	};
	return isCondition && Math.abs(cat.appliedPercentage) <= PCT_TOLERANCE
		? t(
				'{{category}}: {{configured}}% configured, {{applied}}% applied — no inspection; other categories scaled up.',
				vals,
		  )
		: t(
				'{{category}}: {{configured}}% configured, {{applied}}% applied — some inputs had no data.',
				vals,
		  );
};

interface CategoryBlockProps {
	category: BreakdownCategory;
	/** Displayed points per component, in order. */
	points: number[];
	axisTotal: number | null;
	config?: Category;
	text: AxisText;
	t: TFunc;
	fmt: FormatWithUnit;
}

export const CategoryBlock = ({
	category: cat,
	points,
	axisTotal,
	config,
	text,
	t,
	fmt,
}: CategoryBlockProps) => {
	const pct = formatPct(cat.percentage);
	const differs =
		Math.abs(cat.percentage - cat.appliedPercentage) > PCT_TOLERANCE;
	const note = categoryNote(cat, config?.isCondition, t);
	return (
		<Box>
			<Typography
				variant="caption"
				sx={{
					display: 'block',
					mt: 1.25,
					mb: 0.25,
					fontWeight: 700,
					color: 'text.secondary',
				}}>
				{cat.category}
				<span style={{ fontWeight: 400 }}>
					{' '}
					·{' '}
					{differs
						? text.pctOfApplied(
								pct,
								formatPct(cat.appliedPercentage),
						  )
						: text.pctOf(pct)}
				</span>
			</Typography>
			{note && (
				<Typography
					variant="caption"
					sx={{ display: 'block', color: AMBER }}>
					{note}
				</Typography>
			)}
			{cat.components.map((c, ki) => (
				<ComponentRow
					key={c.name}
					component={c}
					points={c.defaultReason === 'noData' ? null : points[ki]}
					axisTotal={axisTotal}
					useHighestScore={config?.useHighestScore}
					t={t}
					fmt={fmt}
				/>
			))}
		</Box>
	);
};

const AxisFooter = ({
	axis,
	reconciled,
	text,
}: {
	axis: BreakdownAxis;
	reconciled: ReconciledAxis;
	text: AxisText;
}) => (
	<Box
		data-cy="explanation-axis-footer"
		sx={{
			mt: 1.5,
			pt: 1,
			borderTop: '1px solid',
			borderColor: 'divider',
		}}>
		{reconciled.displayTotal === null ||
		reconciled.weightedTotal === null ? (
			<Typography variant="body2">{text.noScore}</Typography>
		) : (
			<>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
					}}>
					<Typography
						variant="body2"
						sx={{ color: 'text.secondary' }}>
						{text.totalLabel}
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 700 }}>
						{reconciled.displayTotal.toFixed(2)}
					</Typography>
				</Box>
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						textAlign: 'right',
						color: 'text.secondary',
					}}>
					{text.tieBack(
						formatPct(axis.axisWeighting),
						reconciled.weightedTotal.toFixed(3),
					)}
				</Typography>
			</>
		)}
		{!reconciled.pointsMatchTotal && (
			<Typography
				variant="caption"
				sx={{ display: 'block', color: AMBER }}>
				{text.pointsOff}
			</Typography>
		)}
		{!reconciled.matchesRow && (
			<Typography
				variant="caption"
				sx={{ display: 'block', color: AMBER }}>
				{text.mismatch(
					reconciled.rowValue === null
						? '—'
						: reconciled.rowValue.toFixed(3),
				)}
			</Typography>
		)}
	</Box>
);

const ScoresOnlyBlock = ({ cat }: { cat: ScoresOnlyCategory }) => (
	<Box>
		<Typography
			variant="caption"
			sx={{
				display: 'block',
				mt: 1.25,
				mb: 0.25,
				fontWeight: 700,
				color: 'text.secondary',
			}}>
			{cat.name}
		</Typography>
		{cat.components.map(x => (
			<Box
				key={x.name}
				data-cy="explanation-component"
				sx={{
					display: 'grid',
					gridTemplateColumns: '1fr auto',
					columnGap: 2,
					py: 0.5,
				}}>
				<Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
					{x.name}
				</Typography>
				<Typography
					variant="body2"
					sx={{ fontSize: '0.8125rem', textAlign: 'right' }}>
					{fmtScore(x.score)}
				</Typography>
			</Box>
		))}
	</Box>
);

export const AxisSection = ({
	axisKey,
	axis,
	reconciled,
	scoresOnly,
	configCategories,
}: AxisSectionProps) => {
	const { t } = useGlobalization();
	const { getSystemValueFormatted } = useUnits();
	const fmt: FormatWithUnit = (v, u) =>
		getSystemValueFormatted(`${v}${u}`) ?? `${v}${u}`;
	const text = axisText(t, axisKey);

	return (
		<Box
			sx={{ flex: 1, minWidth: 300 }}
			data-cy={`explanation-axis-${axisKey}`}>
			<Typography
				sx={{
					fontSize: 13,
					fontWeight: 700,
					color: '#0d1520',
					mb: 1.5,
				}}>
				{text.title}
			</Typography>

			{axis && reconciled ? (
				<>
					<Header
						cols={[
							t('Component'),
							t('Value'),
							t('Score × Weight = Points'),
						]}
					/>
					{axis.categories.map((cat, ci) => (
						<CategoryBlock
							key={cat.category}
							category={cat}
							points={reconciled.displayPoints[ci]}
							axisTotal={reconciled.total}
							config={configCategories?.find(
								x => x.name === cat.category,
							)}
							text={text}
							t={t}
							fmt={fmt}
						/>
					))}
					<AxisFooter
						axis={axis}
						reconciled={reconciled}
						text={text}
					/>
				</>
			) : (
				<>
					<Header cols={[t('Component'), t('Score')]} />
					{(scoresOnly ?? []).map(cat => (
						<ScoresOnlyBlock key={cat.name} cat={cat} />
					))}
				</>
			)}
		</Box>
	);
};

export default AxisSection;
